import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import classNames from 'classnames';
import { classnamePrefix } from '../../../../constants';
import { getStyle } from '../../../helpers';

export const ReadMore = ({ children, lines, button, color, className }) => {
  const textRef = useRef(null);
  const cloneRef = useRef(null);

  const [cloneHeight, setCloneHeight] = useState('auto');
  const [maxHeight, setMaxHeight] = useState('0');
  const [state, setState] = useState('closed');

  const isHidden =
    parseInt(cloneHeight, 10) < parseInt(maxHeight, 10) || state === 'opened';

  const handleOnClick = () => {
    if (state === 'closed') {
      setState('opened');
    } else {
      setState('closed');
    }
  };

  const { key } = children;

  useLayoutEffect(() => {
    if (cloneRef?.current) {
      const currentMaxHeight = `${
        parseInt(getStyle(cloneRef.current, 'line-height'), 10) * lines
      }px`;
      const cloneCurrentHeight = `${cloneRef.current.clientHeight}px`;

      requestAnimationFrame(() => {
        if (cloneHeight !== cloneCurrentHeight) {
          setCloneHeight(cloneCurrentHeight);
        }

        if (maxHeight !== currentMaxHeight) {
          setMaxHeight(currentMaxHeight);
        }

        if (cloneHeight === maxHeight) {
          setState('opened');
        }
      });
    }
  }, [lines]);

  useEffect(() => {
    if (state === 'opened') {
      setState('closed');
    }
  }, [key]);

  return (
    <Component _className={className}>
      <Content
        ref={textRef}
        _cloneHeight={cloneHeight}
        _maxHeight={maxHeight}
        _color={color}
        _state={state}
      >
        {children}

        <Clone ref={cloneRef}>{children}</Clone>
      </Content>

      <Button
        onClick={handleOnClick}
        role="button"
        tabIndex="0"
        onKeyDown={getHandleActionKeyDown(handleOnClick)}
        _isHidden={isHidden}
        _state={state}
      >
        {button}
      </Button>
    </Component>
  );
};

const Component = styled.div.attrs(({ _className }) => ({
  className: _className
}))``;

const Content = styled.div.attrs({
  className: `${classnamePrefix}-read-more-content`
})`
  position: relative;
  overflow: hidden;
  transition: max-height 0.3s ease;

  max-height: ${({ _state, _maxHeight, _cloneHeight }) =>
    _state === 'closed' ? _maxHeight : _cloneHeight};

  &:after {
    content: '';
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: ${({ _color }) =>
      `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${
        _color || 'rgba(255, 255, 255, 1)'
      } 100%)`};
    display: ${({ _cloneHeight, _maxHeight, _state }) =>
      parseInt(_cloneHeight) < parseInt(_maxHeight) || _state === 'opened'
        ? 'none'
        : 'block'};
    position: absolute;
    transition: opacity 0.3s ease;
    opacity: ${({ _state }) => (_state === 'closed' ? 1 : 0)};
    pointer-events: none;
  }
`;

const Clone = styled.div.attrs({
  className: `${classnamePrefix}-read-more-clone`
})`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  width: 100%;
  white-space: pre-wrap;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: inherit;
  font-size: inherit;
`;

const ReadMoreButton = styled.div.attrs({
  className: `${classnamePrefix}-info-ReadMoreButton`
})`
  font-weight: 700;
  overflow: hidden;
  opacity: 1;
  display: block;
`;

const Button = styled.div.attrs(({ _isHidden }) => ({
  as: ReadMoreButton || 'div',
  className: classNames(
    `${classnamePrefix}-read-more-button`,
    _isHidden && 'closed'
  )
}))`
  cursor: pointer;
  display: block;
  height: ${({ _isHidden }) => (_isHidden ? '0' : 'auto')};
`;
